import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
} from 'react-native';
import PlatformIcon from '../common/PlatformIcon';

// 直接定义颜色，避免 @cubemeet/core 导入问题
const MODULE_COLOR = '#F97316'; // travel 模块的橙色

// ============ 类型定义 ============

export interface CategoryStatItem {
  /** 类别名称 */
  category: string;
  /** 类别 key (用于图标映射) */
  categoryKey?: string;
  /** 金额 */
  amount: number;
  /** 百分比 */
  percentage: number;
  /** 颜色 */
  color: string;
}

export interface CategoryChartSectionProps {
  /** 类别统计数据 */
  categories: CategoryStatItem[];
  /** 货币符号 */
  currency?: string;
  /** 格式化金额 */
  formatCurrency?: (amount: number, currency?: string) => string;
}

// ============ 辅助函数 ============

const defaultFormatCurrency = (amount: number, currency?: string): string => {
  const symbol = currency || 'HK$';
  return `${symbol}${amount.toFixed(2)}`;
};

// 类别图标映射
const getCategoryIcon = (categoryKey?: string): string => {
  const iconMap: Record<string, string> = {
    food: 'restaurant',
    transport: 'directions-car',
    accommodation: 'hotel',
    activities: 'local-activity',
    shopping: 'shopping-bag',
    documents: 'description',
    other: 'more-horiz',
    general: 'category',
  };
  return iconMap[categoryKey || 'other'] || 'more-horiz';
};

// 默认颜色映射
const DEFAULT_COLORS = [
  '#9CA3AF', // gray - 其他
  '#10B981', // green - 购物
  '#EF4444', // red - 餐饮
  '#3B82F6', // blue - 交通
  '#F59E0B', // yellow - 住宿
  '#8B5CF6', // purple - 活动
];

// ============ 组件 ============

const CategoryChartSection: React.FC<CategoryChartSectionProps> = ({
  categories,
  currency = 'HK$',
  formatCurrency = defaultFormatCurrency,
}) => {
  // 空状态
  if (categories.length === 0) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <PlatformIcon name="donut-large" size={20} color={MODULE_COLOR} family="MaterialIcons" />
          <Text style={styles.title}>类别统计</Text>
        </View>
        <View style={styles.emptyState}>
          <PlatformIcon name="pie-chart" size={48} color="#D1D5DB" family="MaterialIcons" />
          <Text style={styles.emptyText}>暂无类别统计数据</Text>
        </View>
      </View>
    );
  }

  // 按金额排序
  const sortedCategories = [...categories].sort((a, b) => b.amount - a.amount);
  
  // 计算总金额
  const totalAmount = sortedCategories.reduce((sum, cat) => sum + cat.amount, 0);

  // 环形图参数
  const chartSize = 200;
  const center = chartSize / 2;
  const ringWidth = 16;
  const ringSpacing = 18;
  const baseRadius = 50;

  return (
    <View style={styles.container}>
      {/* 标题 */}
      <View style={styles.header}>
        <PlatformIcon name="donut-large" size={20} color={MODULE_COLOR} family="MaterialIcons" />
        <Text style={styles.title}>类别统计</Text>
      </View>

      {/* 环形图 */}
      <View style={styles.chartContainer}>
        <View style={[styles.chartWrapper, { width: chartSize, height: chartSize }]}>
          {/* 同心圆环 - 从外到内绘制 */}
          {sortedCategories.map((cat, index) => {
            const radius = baseRadius + (sortedCategories.length - 1 - index) * ringSpacing;
            const size = radius * 2;
            const offset = center - radius;
            
            return (
              <View
                key={`ring-${index}`}
                style={[
                  styles.ring,
                  {
                    width: size,
                    height: size,
                    borderRadius: radius,
                    borderWidth: ringWidth,
                    borderColor: cat.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
                    position: 'absolute',
                    top: offset,
                    left: offset,
                  },
                ]}
              />
            );
          })}

          {/* 中心内容 */}
          <View style={styles.centerContent}>
            <Text style={styles.totalAmount}>{formatCurrency(totalAmount, currency)}</Text>
            <Text style={styles.totalLabel}>Total</Text>
          </View>
        </View>
      </View>

      {/* 类别列表 */}
      <View style={styles.categoryList}>
        {sortedCategories.map((cat, index) => (
          <View key={`cat-${index}`} style={styles.categoryItem}>
            <View style={styles.categoryLeft}>
              <View style={[styles.categoryIcon, { backgroundColor: `${cat.color}20`, borderColor: cat.color }]}>
                <PlatformIcon 
                  name={getCategoryIcon(cat.categoryKey)} 
                  size={16} 
                  color={cat.color} 
                  family="MaterialIcons" 
                />
              </View>
              <Text style={styles.categoryName}>{cat.category}</Text>
            </View>
            <View style={styles.categoryRight}>
              <Text style={styles.categoryAmount}>{formatCurrency(cat.amount, currency)}</Text>
              <Text style={styles.categoryPercentage}>{cat.percentage.toFixed(1)}%</Text>
            </View>
          </View>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    ...Platform.select({
      web: {
        boxShadow: '0px 2px 8px rgba(0,0,0,0.06)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 8,
        elevation: 2,
      },
    }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    gap: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 32,
  },
  emptyText: {
    marginTop: 12,
    fontSize: 14,
    color: '#9CA3AF',
  },
  // 环形图
  chartContainer: {
    alignItems: 'center',
    marginBottom: 24,
  },
  chartWrapper: {
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'center',
  },
  ring: {
    backgroundColor: 'transparent',
  },
  centerContent: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
  },
  totalAmount: {
    fontSize: 22,
    fontWeight: '700',
    color: MODULE_COLOR,
  },
  totalLabel: {
    fontSize: 13,
    color: '#9CA3AF',
    marginTop: 2,
  },
  // 类别列表
  categoryList: {
    gap: 14,
  },
  categoryItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  categoryLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  categoryIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  categoryName: {
    fontSize: 15,
    fontWeight: '500',
    color: '#374151',
  },
  categoryRight: {
    alignItems: 'flex-end',
  },
  categoryAmount: {
    fontSize: 15,
    fontWeight: '600',
    color: '#111827',
  },
  categoryPercentage: {
    fontSize: 12,
    color: '#9CA3AF',
    marginTop: 2,
  },
});

export default CategoryChartSection;
